import React, { useState } from "react";
import BottomSheet from "./BottomSheet";
import { phoneBar, lineByKey, IN_PLACE } from "./phoneModel.mjs";
import DateField from "../datepicker/DateField";
import { todayYmd } from "../datepicker/calendar.mjs";

/*
 * THE PHONE'S OWN BOTTOM BAR (frame 1h): the screen's one yellow button, and More.
 *
 * The record sheet covers the app's tabs, so this bar is where the thumb is. The
 * yellow is Next's top line's main control (record.next.yellow), the same one the
 * card would have drawn; on the phone the card leaves it out and says "in the bar
 * below", so the screen still has exactly one. More opens a bottom sheet with
 * every other control of Next, each named with its line ("Mark sent · Owed · case
 * study PDF"), then Change status….
 *
 * Review and Move the wake-up are answered inside the sheet (record/phoneModel.mjs
 * IN_PLACE), as NextList answers them inside the card: the waiting changes with
 * Approve and Dismiss, or a date with Save. Their confirming buttons are black,
 * because the yellow was spent on what opened them. A control that fails says so
 * just above the bar, next to where it was tapped, and the draft or the choice is
 * kept.
 *
 * Props:
 *   record   the shell's bundle: next (nextModel), company (recycle_until, for
 *            the wake-up date's starting value)
 *   ops      ops.control(line, control), ops.patchCompany, ops.resolvePending,
 *            ops.closeStatus / ops.openStatus
 */
export default function PhoneBar({ record, ops }) {
  const next = record.next;
  const bar = phoneBar(next);
  // null (closed) | { view: "list" } | { view: "review" | "move_wakeup", key }
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wakeDate, setWakeDate] = useState("");

  const close = () => {
    setSheet(null);
    setError(null);
  };

  const openInPlace = (line, control) => {
    setError(null);
    if (control.id === "move_wakeup") {
      const until = record.company && record.company.recycle_until;
      setWakeDate(until ? String(until).slice(0, 10) : "");
    }
    setSheet({ view: control.id, key: line.key });
  };

  const run = async (line, control) => {
    setError(null);
    if (IN_PLACE.has(control.id)) return openInPlace(line, control);
    setSheet(null);
    try {
      await ops.control(line, control);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const guarded = async (fn, after) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (after) after();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // A sheet view reads its line again from the reloaded model: after the last
  // waiting change is approved, the line is gone and so is the view.
  const viewLine = sheet && sheet.key ? lineByKey(next, sheet.key) : null;
  const shown = !!sheet && !(sheet.key && !viewLine);

  let body = null;
  let title = "More";
  if (sheet && sheet.view === "review" && viewLine) {
    title = viewLine.title;
    body = (
      <div className="rec-sheet__view">
        {(viewLine.items || []).map((item) => (
          <div className="rec-sheet__pending" key={item.id}>
            <div>
              <div>{item.text}</div>
              {item.evidence && <div className="rec-meta">{item.evidence}</div>}
            </div>
            <div className="rec-actionsrow">
              <button type="button" className="rec-btn rec-btn--dark" disabled={busy} onClick={() => guarded(() => ops.resolvePending(item.id, "approve"))}>
                Approve
              </button>
              <button type="button" className="rec-btn rec-btn--plain" disabled={busy} onClick={() => guarded(() => ops.resolvePending(item.id, "dismiss"))}>
                Dismiss
              </button>
            </div>
          </div>
        ))}
        {error && <div className="rec-err" role="alert">{error}</div>}
      </div>
    );
  } else if (sheet && sheet.view === "move_wakeup" && viewLine) {
    title = "Move the wake-up";
    body = (
      <form
        className="rec-sheet__view"
        onSubmit={(e) => {
          e.preventDefault();
          if (wakeDate) guarded(() => ops.patchCompany({ recycle_until: wakeDate }), close);
        }}
      >
        <label className="rec-label" htmlFor="rec-phone-wake">New wake-up date</label>
        <DateField
          id="rec-phone-wake"
          className="rec-sheet__input"
          value={wakeDate}
          onChange={setWakeDate}
          min={todayYmd()}
          quick={[{ label: "1 month", months: 1 }, { label: "3 months", months: 3 }, { label: "6 months", months: 6 }]}
        />
        {error && <div className="rec-err" role="alert">{error}</div>}
        <div className="rec-confirm__actions">
          <button type="button" className="rec-btn rec-btn--plain" onClick={() => setSheet({ view: "list" })}>Back</button>
          <button type="submit" className="rec-btn rec-btn--dark" disabled={!wakeDate || busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    );
  } else {
    body = (
      <>
        {bar.items.map((item) => (
          <button key={item.key} type="button" className="rec-sheet__item" onClick={() => run(item.line, item.control)}>
            <span className="rec-sheet__label">{item.label}</span>
            <span className="rec-meta rec-sheet__meta">{item.meta}</span>
          </button>
        ))}
        {ops.roomCompany && ops.openRoom && (
          <button
            type="button"
            className="rec-sheet__item"
            onClick={() => {
              close();
              ops.openRoom();
            }}
          >
            <span className="rec-sheet__label">Prospect Room</span>
            <span className="rec-meta" aria-hidden="true">›</span>
          </button>
        )}
        <button
          type="button"
          className="rec-sheet__item"
          onClick={() => {
            close();
            // Opens as its own sheet from the header; closed first because
            // openStatus toggles.
            ops.closeStatus();
            ops.openStatus({ scroll: true });
          }}
        >
          <span className="rec-sheet__label">Change status…</span>
          <span className="rec-meta" aria-hidden="true">›</span>
        </button>
      </>
    );
  }

  return (
    <>
      {error && !sheet && <div className="rec-phonebar__err rec-err" role="alert">{error}</div>}
      <div className="rec-phonebar">
        {bar.primary && (
          <button type="button" className="rec-btn rec-btn--primary" onClick={() => run(bar.primary.line, bar.primary.control)}>
            {bar.primary.control.label}
          </button>
        )}
        <button
          type="button"
          className={`rec-btn rec-phonebar__more${bar.primary ? "" : " rec-btn--block"}`}
          aria-haspopup="dialog"
          aria-expanded={shown}
          onClick={() => {
            setError(null);
            setSheet({ view: "list" });
          }}
        >
          More
        </button>
      </div>
      <BottomSheet open={shown} title={title} onClose={close}>
        {body}
      </BottomSheet>
    </>
  );
}
